// Internal core-api → ai-svc contract for POST /internal/classify-finding
// (ADR-0058 v2; widened by ADR-0119 PR-3 for the reclassify lane).
//
// Until ADR-0119 this schema lived only inside ai-svc's route, and the two
// shipped core-api callers built the body as an inline literal and CAST
// the response (`as { findings: … }`) — a producer↔consumer contract with
// no shared type, the same class of drift `queue-payloads-defined-in-
// api-types` closed for pg-boss. PR-3 adds a third producer and four new
// fields, which is exactly when a typo in `verifiedPhotoIndices` becomes a
// runtime 400 with no compile error — on the field that is the gate's
// security input. So the contract moves here: ai-svc parses the request
// with it, core-api types its body and its response against it.
//
// This deliberately departs from ai.ts's "internal payloads are
// backend-private" note for THIS route only; /internal/chat keeps its
// route-local schema until it grows a second producer.
//
// `lane` is orthogonal to `mode`. `mode` is the prompt-shell axis
// (walkthrough | standalone_finding, ADR-0111); `lane` is who is calling
// and with what clamp guarantees. A reclassify runs under the same shell
// as its own intake — today, omitted for broker_postventa — never under
// standalone_finding, or the reclassification proposal would run under
// different prompt rules than the original proposal for the SAME row and
// handoff rule 5's confidence drop would be confounded with a shell change.
import { taxonomyHintsSchema, } from "@bitacorapro/core-engine";
import { z } from "zod";
export const INTERNAL_CLASSIFY_FINDING_MAX_PHOTOS = 10;
// ADR-0119 timeout ladder — the ceiling every core-api caller of
// /internal/classify-finding awaits the fetch behind (reports.ts's
// /hallazgos, assets.ts's /observations, findings.ts's reclassify),
// one rung under the 60s Railway edge and one rung over ai-svc's own
// 42s ROUTE_ABORT_MS. A single shared constant so the three callers
// can never drift out of that ordering independently.
export const INTERNAL_CLASSIFY_FINDING_CALLER_TIMEOUT_MS = 45_000;
// Frozen error vocabulary for a classify-finding upstream failure —
// same shape as support.ts's SUPPORT_GRANT_ERROR_CODES (array + z.enum
// + inferred type), so core-api's three callers share one code instead
// of each hand-typing the "CLASSIFIER_UNAVAILABLE" string literal.
export const CLASSIFY_FINDING_ERROR_CODES = ["CLASSIFIER_UNAVAILABLE"];
export const classifyFindingErrorCodeSchema = z.enum(CLASSIFY_FINDING_ERROR_CODES);
export const internalClassifyFindingRequest = z
    .object({
    organizationId: z.string().uuid(),
    recipeSlug: z.string().min(1),
    /** Inspector's note — already transcribed client-side (Web Speech
     *  API). Optional: a submission can be photos-only. On the reclassify
     *  lane core-api sends the finding's `originalText ?? humanDescription`
     *  here — never rendered into the cached system prefix. */
    note: z.string().trim().max(4000).optional(),
    // ADR-0111: min(0) — a note-only hallazgo is valid; the superRefine
    // below keeps "at least one of photo or note" as the real floor.
    // max(10) is the cost ceiling on an image-dominated call. On the
    // reclassify lane core-api caps the outgoing array itself (detalle
    // newest-first, then contexto) and computes verifiedPhotoIndices
    // against the array it actually sends.
    photoUrls: z.array(z.string().url()).max(INTERNAL_CLASSIFY_FINDING_MAX_PHOTOS),
    /** ADR-0111 — "standalone_finding" flips the prompt shell to
     *  exactly-one + note-primacy rules (the caller's recipe declares a
     *  standalone_finding reportType). Omitted = the original
     *  walkthrough-derived behavior, which postventa's existing hallazgo
     *  flow keeps. */
    mode: z.enum(["walkthrough", "standalone_finding", "reclassify_single"]).optional(),
    /** ADR-0119 — which clamp policy applies. Omitted = "intake". */
    lane: z.enum(["intake", "reclassify"]).optional(),
    /**
     * Positions in `photoUrls` that are `detalle` evidence NOT yet
     * consumed by a prior classification run — derived server-side by
     * core-api from its own `evidence` rows, in the same pass and the
     * same order that built `photoUrls`. Required (an EMPTY array is
     * valid and means a hints-only run) when lane = "reclassify";
     * forbidden otherwise.
     */
    verifiedPhotoIndices: z
        .array(z.number().int().nonnegative())
        .max(INTERNAL_CLASSIFY_FINDING_MAX_PHOTOS)
        .optional(),
    /** What the supervisor confirmed in the field (P5/P6). Reclassify
     *  lane only. */
    humanHints: taxonomyHintsSchema.optional(),
    /** Free-text context the supervisor dictated when triggering a
     *  reclassify with new voice evidence but no new photo and no
     *  differing structured hint (ai-ml-engineer review, contract
     *  widening). Reclassify lane only — same scoping as `humanHints`.
     *  Rendered in ai-svc's USER turn only, never the cached system
     *  block (per-request, like `humanHints`). Distinct from `note`:
     *  `note` is the immutable original intake transcription, this is
     *  new per-request context for THIS reclassify call. It informs the
     *  location/failure-type axes and the description the same way
     *  `humanHints` does — it never feeds the confidence clamp or
     *  `evidenciaVerificada`, which stay keyed on `verifiedPhotoIndices`
     *  alone. */
    supervisorNote: z.string().trim().max(4000).optional(),
    /** Who wrote `note` (demo postventa 2026-10-08). Omitted = "supervisor",
     *  the wording every caller had before this field existed, so old
     *  callers keep their prompt byte for byte. "owner" is sent only by
     *  the owner classify route (POST /assets/:id/observations/classify):
     *  ai-svc then labels the note as the owner's in the USER turn and
     *  tells the model not to attribute it to an inspector nor assume a
     *  pre-delivery deadline — the house is already in warranty. Never
     *  rendered into the cached system block. */
    reporter: z.enum(["supervisor", "owner"]).optional(),
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.photoUrls.length === 0 && !(body.note && body.note.length > 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["photoUrls"],
            message: "at least one photo or a note is required",
        });
    }
    if (body.lane === "reclassify") {
        if (body.verifiedPhotoIndices === undefined) {
            // The single most likely implementation slip: a reclassify body
            // without the field would silently fall back to the intake clamp
            // and open the gate with zero verified evidence. Fail loud here —
            // this is a core-api bug, not a model quirk.
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["verifiedPhotoIndices"],
                message: "verifiedPhotoIndices is required on the reclassify lane (empty = hints-only)",
            });
        }
        else {
            for (const i of body.verifiedPhotoIndices) {
                if (i >= body.photoUrls.length) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["verifiedPhotoIndices"],
                        message: `verified index ${i} is outside photoUrls (${body.photoUrls.length})`,
                    });
                    break;
                }
            }
        }
        if (body.mode === "standalone_finding") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["mode"],
                message: "a reclassify runs under its intake's shell, never standalone_finding",
            });
        }
        if (body.reporter !== undefined) {
            // The reclassify lane labels `note` as the row being re-evaluated,
            // not as anyone's capture — a reporter label there would contradict it.
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["reporter"],
                message: "reporter is only accepted on the intake lane",
            });
        }
    }
    else {
        // #0654 — el espejo de la regla de arriba: `reclassify_single`
        // enmarca el turno de usuario como "reevaluá ESTA observación ya
        // registrada", lo que es falso en cualquier carril de intake, donde
        // no hay fila previa que reevaluar.
        if (body.mode === "reclassify_single") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["mode"],
                message: "reclassify_single is only coherent on the reclassify lane",
            });
        }
        if (body.verifiedPhotoIndices !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["verifiedPhotoIndices"],
                message: "verifiedPhotoIndices is only accepted on the reclassify lane",
            });
        }
        if (body.humanHints !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["humanHints"],
                message: "humanHints is only accepted on the reclassify lane",
            });
        }
        if (body.supervisorNote !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["supervisorNote"],
                message: "supervisorNote is only accepted on the reclassify lane",
            });
        }
    }
});
//# sourceMappingURL=internal-classify-finding.js.map